import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // active:scale-[0.97] is the whole trick behind buttons feeling physical —
  // a press that gives slightly under the finger reads as a real control.
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium',
    'transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)]',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-[0.97]',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-farm-600 text-white shadow-soft hover:bg-farm-700 hover:shadow-lift hover:-translate-y-0.5 sheen',
        harvest:
          'bg-harvest-500 text-soil-900 shadow-soft hover:bg-harvest-400 hover:shadow-lift hover:-translate-y-0.5 sheen',
        secondary: 'bg-soil-100 text-soil-900 hover:bg-soil-200',
        outline:
          'border border-soil-300 bg-transparent text-soil-800 hover:border-farm-500 hover:text-farm-700 hover:bg-farm-50',
        ghost: 'bg-transparent text-soil-700 hover:bg-soil-100 hover:text-soil-900',
        danger: 'bg-danger text-white hover:brightness-110 shadow-soft',
        link: 'bg-transparent text-farm-700 underline-offset-4 hover:underline p-0 h-auto active:scale-100',
      },
      size: {
        sm: 'h-9 rounded-lg px-3 text-sm [&_svg]:size-4',
        md: 'h-11 rounded-xl px-5 text-sm [&_svg]:size-4',
        lg: 'h-13 rounded-xl px-7 text-base [&_svg]:size-5',
        icon: 'size-10 rounded-xl [&_svg]:size-4',
      },
      full: { true: 'w-full' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  loadingText?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, full, isLoading, loadingText, children, disabled, ...props },
    ref,
  ) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size, full }), className)}
      disabled={disabled || isLoading}
      // Tells a screen reader the control is busy rather than silently
      // ignoring the click.
      aria-busy={isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <Loader2 className="animate-spin" aria-hidden />
          {loadingText ?? children}
        </>
      ) : (
        children
      )}
    </button>
  ),
);

Button.displayName = 'Button';
